/**
 * AISidebar · 入口触发按钮
 *
 * 对应文档：docs/AI-Photo-Category/DEV_BRIEF.md §2.3 AITriggerButton
 *
 * 用途：
 *  · 页面顶部 / 空态页面主体作为"打开 AI 智能分类"的入口
 *  · 预留角标（badge）表达"后台有未读进度"等状态
 *
 * 本组件不接 Redux，所有状态通过 props 传入。
 * 视觉对齐 Claude Design 原型 · 用 Sparkle SVG 替代 antd 内置 thunderbolt 图标。
 */

import React from 'react'
import { Button, Badge } from 'antd'
import styles from './AISidebar.module.scss'

interface AITriggerButtonProps {
  /** 点击回调 · 通常用于打开 AISidebar */
  onClick: () => void
  /** 是否显示角标 · 默认 false */
  hasBadge?: boolean
  /** 角标自定义文案 · 传空则显示小圆点 */
  badgeText?: string
  /** 自定义类名 */
  className?: string
  /** 按钮文案 · 默认 "AI 智能分类" · 支持如切到 "收起对话" 等场景 */
  label?: string
  /**
   * 描边态 · 主色边框 + 白底
   *
   * 列表页右上角与"新建文件夹"主按钮（type=primary 实心）并排时，
   * 用 ghost 让 AI 助手降一级权重 + 视觉互斥，避免双主按钮抢注意力。
   */
  ghost?: boolean
}

/** Sparkle 图标 · 与 FolderCard AI 徽标同款星形
 *  verticalAlign -2px 对齐 antd 内置图标的 -0.125em；marginRight 8px
 *  与 antd 按钮 icon+文字 的默认间距一致 */
const SparkleIcon: React.FC = () => (
  <svg
    width={14}
    height={14}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    style={{ marginRight: 8, verticalAlign: '-2px' }}
  >
    <path
      d="M8 1.5L9.3 5.8C9.5 6.4 10 6.9 10.6 7.1L14.9 8.4C15.2 8.5 15.2 8.9 14.9 9L10.6 10.3C10 10.5 9.5 11 9.3 11.6L8 15.9C7.9 16.2 7.5 16.2 7.4 15.9L6.1 11.6C5.9 11 5.4 10.5 4.8 10.3L0.5 9C0.2 8.9 0.2 8.5 0.5 8.4L4.8 7.1C5.4 6.9 5.9 6.4 6.1 5.8L7.4 1.5C7.5 1.2 7.9 1.2 8 1.5Z"
      fill="currentColor"
    />
  </svg>
)

const AITriggerButton: React.FC<AITriggerButtonProps> = ({
  onClick,
  hasBadge = false,
  badgeText,
  className,
  label = 'AI 智能分类',
  ghost = false
}) => {
  const button = (
    <Button
      type="primary"
      ghost={ghost}
      onClick={onClick}
      className={className}
    >
      <SparkleIcon />
      {label}
    </Button>
  )

  // 无角标时直接返回按钮，避免多余的 Badge 包裹
  if (!hasBadge) return button

  // badgeText 非空显示带文案角标，否则 dot 形态
  if (badgeText) {
    return (
      <Badge count={badgeText} className={styles.triggerBadge}>
        {button}
      </Badge>
    )
  }
  return (
    <Badge dot className={styles.triggerBadge}>
      {button}
    </Badge>
  )
}

export default AITriggerButton
